import { decodeGlobalId } from "~/lib/relayUtil";

// I am trying to contain the abomination in this file as much as possible
//
// Here's the deal:
// - on wide viewports, the comments are inside the stream notes, but on narrow
//   ones they're in a wholly separate tab
// - the existing setup uses the eventEmitter thing to pass state around, which means
//   there's no source of truth for the state of what comment thread is open, meaning
//   we can't just use the same state for the panel and the tab. I'd rather refactor
//   that so we can reuse it, but it would take time, which I don't have.
// - permalinks to comments use the note ID, block key, _and_ thread ID, the latter
//   of which we don't have access to in the comments prose plugin
// - so we need the URLs for the comments tab to be generateable by the plugin,
//   but the existing URLs for the comments panel to still work
//
// This means the URL segments for comments in the panel and the tab use different formats.
// These functions are for converting/generating/parsing them.
//
// In this file "URL fragment" means the format the comments **panel** and server-generated
// permalinks use: `#[Note GraphQL ID]/[NoteBlock key]/comments/[Thread GraphQL ID]/`
//
// "PathForTab" means the format the comments **tab** uses: `[Note GraphQL ID];[Thread key]`
//
// "Thread key" is the format the GraphQL API uses: `NoteBlock:[Note primary key]:[Block key]`
//
// "Block key" is the block's `key` field, often but not always a UUID.

/**
 * Comment permalinks use hashes (document fragments) to link to a specific comment thread.
 * This function extracts the thread key from the hash.
 *
 * @param urlFragment These are in the format `#[Note GraphQL ID]/[NoteBlock key]/comments/[Thread GraphQL ID]/`
 */
export function threadKeyFromUrlFragment(urlFragment: string): string {
	const raw = urlFragment.startsWith("#") ? urlFragment.slice(1) : urlFragment;
	const [noteId, blockKey, section, _argument] = raw.split("/");
	if (section !== "comments") throw new Error("Not a comments url");
	return threadKeyFromNoteIdAndBlockKey(noteId, blockKey);
}

/**
 * Comment threads' keys for Note comments are based on the GraphQL ID of the note and key of the block.
 */
export function threadKeyFromNoteIdAndBlockKey(noteId: string, blockKey: string): string {
	const [, notePk] = decodeGlobalId(noteId);
	return `NoteBlock:${notePk}:${blockKey}`;
}

/**
 * Comment threads' keys for recap are based on the recording ID and timestamps.
 * ex: "MeetingRecording:<based64-recording-id>:11.22:15.135" - means, that the comment left on the transcript
 * segment [11.22 - 15.135] and it belongs to a particular recording
 */
export function threadKeyFromRecapIdAndTimes(recordingId: string, start: number, end: number) {
	const [, recordingPk] = decodeGlobalId(recordingId);
	return `MeetingRecording:${recordingPk}:${start}:${end}`;
}

/**
 * To link to the tab on narrow widths, we use the note ID and block key, because
 * we don't actually have access to the thread's GraphQL ID on the front-end.
 */
export function toPathForTab(
	info: {
		/** The GraphQL ID of the note */
		noteId: string;
	} & (
		| {
				/** The block's `key` field, often but not always a UUID. */
				blockKey: string;
		  }
		| {
				/** The thread's key, which looks like `NoteBlock:123:[Block key]` */
				threadKey: string;
		  }
	),
) {
	return `${info.noteId};${
		"blockKey" in info ? threadKeyFromNoteIdAndBlockKey(info.noteId, info.blockKey) : info.threadKey
	}`;
}

/**
 * The inverse of `toPathForTab`, so it can be passed on to the comments panel.
 */
export function fromPathForTab(idForTab: string) {
	const [noteId, threadKey] = idForTab.split(";");
	return { noteId, threadKey };
}
